import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    Box,
    Text,
    Flex,
    HStack,
    Icon,
    IconButton,
    Spinner,
    Center,
} from '@chakra-ui/react';
import React from 'react';
import { TbX } from 'react-icons/tb';
import { useInventoryExportHistory } from '../../hooks/inventory/useExportInventoryHistory';
import { useForceSidebarCollapsed } from '../../contexts/SidebarCollapseOverrideContext';
import { INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../sideDrawerContainerProps';
import type { InventoryExportHistoryEntry } from '../../types/inventory';

function formatExportMode(mode: InventoryExportHistoryEntry['mode']): string {
    return mode === 'all' ? 'All' : 'Filtered';
}

interface InventoryExportHistoryDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    highlightExportId?: string | null;
}

const InventoryExportHistoryDrawer: React.FC<InventoryExportHistoryDrawerProps> = ({
    isOpen,
    onClose,
    highlightExportId,
}) => {
    useForceSidebarCollapsed(isOpen);
    const { data, isLoading, isError } = useInventoryExportHistory(isOpen);
    const exports = data?.exports ?? [];

    return (
        <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md" closeOnOverlayClick>
            <DrawerOverlay />
            <DrawerContent data-partbay-drawer="" motionProps={INSTANT_SIDE_DRAWER_MOTION_PROPS}>
                <Flex direction="column" h="100%">
                    <Flex
                        align="center"
                        justify="space-between"
                        px={5}
                        h="topbar"
                        flexShrink={0}
                        borderBottom="1px solid"
                        borderColor="border.default"
                        bg="bg.subtle"
                    >
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                            Export History
                        </Text>
                        <IconButton
                            aria-label="Close"
                            icon={<Icon as={TbX as React.ElementType} />}
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                        />
                    </Flex>

                    <Box flex={1} overflowY="auto" px={5} py={4} bg="white">
                        {isLoading && (
                            <Center py={12}>
                                <Spinner color="#0071E3" />
                            </Center>
                        )}
                        {isError && (
                            <Text fontSize="sm" color="red.500" textAlign="center" py={8}>
                                Failed to load export history
                            </Text>
                        )}
                        {!isLoading && !isError && exports.length === 0 && (
                            <Text
                                fontSize="sm"
                                color="gray.400"
                                fontStyle="italic"
                                textAlign="center"
                                py={8}
                            >
                                No exports yet
                            </Text>
                        )}
                        {!isLoading && !isError && exports.length > 0 && (
                            <Flex direction="column" gap={2}>
                                {exports.map((entry) => {
                                    const isHighlighted = highlightExportId === entry.exportId;
                                    return (
                                        <Box
                                            key={entry.exportId}
                                            borderWidth="1px"
                                            borderColor={isHighlighted ? '#0071E3' : 'gray.200'}
                                            borderRadius="lg"
                                            bg={isHighlighted ? '#0071E30D' : 'white'}
                                            p={3}
                                        >
                                            <Flex align="center" justify="space-between" gap={2}>
                                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                    {formatExportMode(entry.mode)} Export
                                                </Text>
                                                <Text fontSize="xs" color="gray.500" flexShrink={0}>
                                                    {new Date(entry.createdAt).toLocaleString()}
                                                </Text>
                                            </Flex>
                                            {entry.userName ? (
                                                <Text fontSize="xs" color="gray.600" mt={0.5}>
                                                    {entry.userName}
                                                </Text>
                                            ) : null}
                                            <HStack spacing={1.5} mt={1}>
                                                <Text fontSize="xs" color="gray.600">
                                                    {entry.rowCount} row{entry.rowCount === 1 ? '' : 's'}
                                                </Text>
                                                {entry.filename && (
                                                    <>
                                                        <Text fontSize="xs" color="gray.400">
                                                            ·
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.600" isTruncated>
                                                            {entry.filename}
                                                        </Text>
                                                    </>
                                                )}
                                            </HStack>
                                        </Box>
                                    );
                                })}
                            </Flex>
                        )}
                    </Box>
                </Flex>
            </DrawerContent>
        </Drawer>
    );
};

export default InventoryExportHistoryDrawer;
